import React, { useEffect, useMemo, useState } from 'react';
import { Brain, FlaskConical, Loader2, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Patient } from '@/lib/types';
import {
  aiRecommendationService,
  parseRecommendation,
} from '@/services/aiRecommendationService';
import {
  isStructuredRecommendation,
  isLabRecommendation,
  StructuredRecommendation,
} from '@/types/intellihealthStructured';

interface LabInsightPopoverProps {
  patient: Patient | null;
  biomarker: { name: string; value?: string | number; unit?: string; loinc?: string };
}

/**
 * Tiny popover surfacing IntelliHealth's interpretation for a specific
 * biomarker. Replaces the rail-based lab insight panel; the insight pops
 * inline next to the result.
 */
export default function LabInsightPopover({
  patient,
  biomarker,
}: LabInsightPopoverProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [structured, setStructured] = useState<StructuredRecommendation | null>(null);
  const [markdown, setMarkdown] = useState<string>('');

  useEffect(() => {
    if (!open || !patient?.id) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const recs = await aiRecommendationService.getPatientRecommendations(
          patient.id,
          1,
        );
        if (cancelled) return;
        const parsed = parseRecommendation(recs.data?.recommendation);
        if (
          typeof parsed === 'object' &&
          parsed &&
          isStructuredRecommendation(parsed)
        ) {
          setStructured(parsed);
          setMarkdown('');
        } else if (typeof parsed === 'string') {
          setMarkdown(parsed);
          setStructured(null);
        }
      } catch {
        /* fail silently — show empty state */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, patient?.id]);

  const matched = useMemo(() => {
    const term = biomarker.name?.toLowerCase().trim();
    if (!term) return [];
    const corpus: string[] = [];
    if (structured && isLabRecommendation(structured)) {
      const soap = structured.soap;
      if (soap?.objective) corpus.push(soap.objective);
      if (soap?.assessment) corpus.push(soap.assessment);
      if (soap?.plan) corpus.push(soap.plan);
      if (structured.summary) corpus.push(structured.summary);
    } else if (markdown) {
      corpus.push(markdown);
    }
    return corpus
      .flatMap((text) =>
        text
          .split(/(?<=[.!?])\s+/)
          .map((s) => s.trim())
          .filter((s) => s.toLowerCase().includes(term)),
      )
      .slice(0, 4);
  }, [biomarker.name, structured, markdown]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          className='inline-flex h-7 w-7 items-center justify-center rounded-md text-teal-600 transition-colors hover:bg-teal-50 hover:text-teal-700'
          title='IntelliHealth insight'
          aria-label={`IntelliHealth insight for ${biomarker.name}`}
        >
          <Sparkles className='h-4 w-4' />
        </button>
      </PopoverTrigger>
      <PopoverContent side='left' align='start' className='w-80 p-0'>
        <div className='flex items-center gap-2 border-b border-slate-200 bg-gradient-to-r from-teal-50 to-white px-3 py-2'>
          <FlaskConical className='h-4 w-4 text-teal-600' />
          <div className='min-w-0 flex-1'>
            <div className='truncate text-sm font-semibold text-slate-900'>
              {biomarker.name}
            </div>
            {biomarker.value != null && (
              <div className='flex flex-wrap gap-1'>
                <Badge variant='outline' className='text-[10px]'>
                  {biomarker.value}
                  {biomarker.unit ? ` ${biomarker.unit}` : ''}
                </Badge>
                {biomarker.loinc && (
                  <Badge variant='outline' className='text-[10px]'>
                    LOINC {biomarker.loinc}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>

        <div className='max-h-72 overflow-y-auto px-3 py-2.5'>
          {loading ? (
            <div className='flex items-center gap-2 text-xs text-slate-500'>
              <Loader2 className='h-3.5 w-3.5 animate-spin' /> Loading insight…
            </div>
          ) : matched.length > 0 ? (
            <ul className='space-y-2'>
              {matched.map((sentence, i) => (
                <li
                  key={i}
                  className='rounded-md border border-teal-100 bg-teal-50/40 px-2.5 py-2 text-xs leading-relaxed text-slate-700'
                >
                  {sentence}
                </li>
              ))}
            </ul>
          ) : (
            <div className='flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs text-slate-600'>
              <Brain className='mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400' />
              <span>
                The latest recommendation doesn't mention {biomarker.name}{' '}
                directly. Open the Summary tab for the full picture or ask
                IntelliHealth in chat.
              </span>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
